import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { 
  Send, 
  Paperclip, 
  Smile, 
  Mic, 
  Image as ImageIcon,
  Plus
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import EmojiPicker, { Theme, SkinTonePickerLocation } from 'emoji-picker-react';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
  onAttachFile?: () => void;
  onAttachImage?: () => void;
  onStartRecording?: () => void;
  isRecording?: boolean;
  recordingComponent?: React.ReactNode;
}

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder = "Scrivi un messaggio...",
  disabled = false,
  onAttachFile,
  onAttachImage,
  onStartRecording,
  isRecording = false,
  recordingComponent
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        onSend();
      }
    }
  };

  const handleSend = () => {
    if (value.trim() && !disabled) {
      onSend();
    }
  };

  const handleEmojiClick = (emojiData: any) => {
    onChange(value + emojiData.emoji);
    // Non chiudere automaticamente il picker per permettere selezioni multiple
  };

  return (
    <div className="w-full max-w-full overflow-hidden">
      <div className="flex items-end space-x-2 sm:space-x-3 p-2">
        {/* Attachment Button */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 rounded-full hover:bg-primary/10 transition-smooth shrink-0"
            >
              <Plus className="w-5 h-5 text-primary" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-2 glass border-border/30" side="top">
            <div className="grid gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={onAttachImage}
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Immagine
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={onAttachFile}
              >
                <Paperclip className="w-4 h-4 mr-2" />
                File
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Message Input Container */}
        <div className="flex-1 relative min-w-0">
          <div className="glass rounded-3xl border border-border/30 px-3 sm:px-4 py-3 pr-10 sm:pr-12 focus-within:ring-2 focus-within:ring-primary/30 transition-all hover:border-border/50">
            <Textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={disabled}
              className="resize-none border-0 p-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/70 min-h-[24px] max-h-[120px] w-full"
              rows={1}
              style={{
                height: 'auto',
                minHeight: '24px'
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
            />
            
            {/* Emoji Button */}
            <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 sm:right-2 bottom-2 h-6 w-6 sm:h-8 sm:w-8 p-0 rounded-full hover:bg-primary/10 transition-smooth"
                >
                  <Smile className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-0" side="top" align="end">
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme={Theme.DARK}
                  skinTonePickerLocation={SkinTonePickerLocation.PREVIEW}
                  height={400}
                  width={350}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Send/Voice Button */}
        {isRecording ? (
          <div className="shrink-0 max-w-[120px] sm:max-w-none overflow-hidden">
            {recordingComponent}
          </div>
        ) : value.trim() ? (
          <Button
            onClick={handleSend}
            disabled={disabled || !value.trim()}
            className="h-10 w-10 p-0 rounded-full gradient-primary shadow-primary/20 shadow-lg hover:shadow-primary/30 hover:scale-105 transition-all shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={onStartRecording}
            className="h-10 w-10 p-0 rounded-full glass border border-border/30 hover:bg-primary/10 hover:scale-105 transition-all shrink-0"
          >
            <Mic className="w-4 h-4 text-primary" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChatInput;